import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { GamifiedButton } from "@/components/GamifiedButton";
import { Logo } from "@/components/Logo";
import { ErrorMessage } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";

const ROLES = [
  { id: "student", label: "Élève" },
  { id: "parent", label: "Parent" },
  { id: "teacher", label: "Professeur" },
];
const AGES = [
  { id: "enfant", label: "Enfant" },
  { id: "adolescent", label: "Ado" },
  { id: "adulte", label: "Adulte" },
];

export default function Register() {
  useSeo("Créer un compte", "Crée ton compte Quran Coach gratuit : apprends l'alphabet arabe, mémorise les sourates et suis tes progrès. Élève, parent ou professeur.");
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "student", age_category: "enfant" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await register(form); navigate("/accueil"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 islamic-bg">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg bg-white rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.06)] p-8 border border-slate-100">
        <div className="flex justify-center mb-6"><Logo size="lg" /></div>
        <h1 className="text-3xl font-black text-slate-800 mb-1 text-center">Créer un compte</h1>
        <p className="text-slate-500 mb-6 text-center">Commence ton parcours de mémorisation.</p>

        {error && <div className="mb-4"><ErrorMessage message={error} /></div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input data-testid="register-firstname" required placeholder="Prénom" value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                className="w-full pl-12 pr-3 py-3.5 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
            </div>
            <input data-testid="register-lastname" placeholder="Nom" value={form.last_name}
              onChange={(e) => set("last_name", e.target.value)}
              className="w-full px-4 py-3.5 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
          </div>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input data-testid="register-email" type="email" required placeholder="Adresse email" value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input data-testid="register-password" type="password" required minLength={6} placeholder="Mot de passe (min. 6)" value={form.password}
              onChange={(e) => set("password", e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Je suis</label>
            <div className="grid grid-cols-3 gap-2">
              {ROLES.map((r) => (
                <button key={r.id} type="button" data-testid={`role-${r.id}`} onClick={() => set("role", r.id)}
                  className={cn("py-2.5 rounded-2xl font-bold text-sm border-2 transition-colors",
                    form.role === r.id ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500")}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {form.role === "student" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Catégorie d'âge</label>
              <div className="grid grid-cols-3 gap-2">
                {AGES.map((a) => (
                  <button key={a.id} type="button" data-testid={`age-${a.id}`} onClick={() => set("age_category", a.id)}
                    className={cn("py-2.5 rounded-2xl font-bold text-sm border-2 transition-colors",
                      form.age_category === a.id ? "border-secondary bg-secondary/10 text-secondary" : "border-slate-200 text-slate-500")}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <GamifiedButton type="submit" disabled={loading} className="w-full" data-testid="register-submit">
            {loading ? "Création…" : "Créer mon compte"}
          </GamifiedButton>
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          Déjà un compte ?{" "}
          <Link to="/login" data-testid="go-login" className="font-extrabold text-primary hover:underline">Se connecter</Link>
        </p>
      </motion.div>
    </div>
  );
}
